import { useState, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export interface ActiveUsers {
  consumer: number;
  business: number;
  total: number;
  asOf: number;
}

const POLL_INTERVAL_MS = 30_000;

export const useActiveUsers = (): { data: ActiveUsers | null; loading: boolean; error: string | null } => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<ActiveUsers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchActiveUsers = async (): Promise<void> => {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL environment variable is not set');
          setLoading(false);
        }
        return;
      }

      try {
        let token: string | null = null;
        const currentAccounts = accountsRef.current;
        if (currentAccounts.length > 0) {
          token = await acquireApiToken(instance, currentAccounts[0]);
        }

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${apiUrl}/admin/active-users`, { method: 'GET', headers });
        const body = await response.json();

        if (!response.ok) {
          throw new Error(body.error || `HTTP ${response.status}`);
        }
        if (!body.success || !body.data) {
          throw new Error(body.error || 'Failed to fetch active users');
        }

        if (isMounted) {
          setData(body.data as ActiveUsers);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const tick = async (): Promise<void> => {
      await fetchActiveUsers();
      if (isMounted) {
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, instance]);

  return { data, loading, error };
};
